/**
 * SongAI Flow project-level beat-sync animation preferences (stored on project.input).
 * UI slider 0–100; stored/API value is motion rate 0 (stopped) … 1 (normal).
 */

import {
  MOTION_SPEED_SLIDER_MAX,
  MOTION_SPEED_SLIDER_MIN,
  clampMotionRate,
  formatMotionSpeedLabel,
  motionRateFromSlider,
  motionSliderFromRate,
} from './motionSpeedScale';

export const SONG_ANIMATION_SPEED_MIN = 0;
export const SONG_ANIMATION_SPEED_MAX = 1;

export function clampSongAnimationSpeed(value) {
  return clampMotionRate(value, SONG_ANIMATION_SPEED_MAX);
}

export function songAnimationSpeedToSlider(speed) {
  return motionSliderFromRate(speed, SONG_ANIMATION_SPEED_MAX);
}

export function songAnimationSpeedFromSlider(sliderValue) {
  return motionRateFromSlider(sliderValue, SONG_ANIMATION_SPEED_MAX);
}

/** Default motion rate when project has no explicit speed (slower, smoother song flow). */
export const SONG_ANIMATION_SPEED_DEFAULT = 0.38;

/** Beat-sync slider: 100% → every beat; slow end → every 10th beat. */
export const BEAT_SYNC_EVERY_AT_FULL = 1;
export const BEAT_SYNC_EVERY_AT_SLOW = 10;
/** Motion punch multiplier at slider extremes (pan/zoom/shake scale). */
export const BEAT_SYNC_INTENSITY_AT_FULL = 3.5;
export const BEAT_SYNC_INTENSITY_AT_SLOW = 0.35;

function smoothstep01(t) {
  const x = Math.max(0, Math.min(1, t));
  return x * x * (3 - 2 * x);
}

/**
 * Map beat-sync speed (0…1) to beat cadence: 1 = every beat, 10 = every 10th beat.
 * Uses the same 1–100 slider curve as other motion speeds.
 */
export function beatAnimateEveryFromSongSpeed(speed) {
  const s = clampSongAnimationSpeed(speed);
  if (s <= 0) return 999;
  const slider = songAnimationSpeedToSlider(s);
  if (slider >= MOTION_SPEED_SLIDER_MAX) return BEAT_SYNC_EVERY_AT_FULL;
  if (slider <= 1) return BEAT_SYNC_EVERY_AT_SLOW;
  const t = smoothstep01((slider - 1) / (MOTION_SPEED_SLIDER_MAX - 1));
  const every = BEAT_SYNC_EVERY_AT_SLOW + (BEAT_SYNC_EVERY_AT_FULL - BEAT_SYNC_EVERY_AT_SLOW) * t;
  return Math.max(BEAT_SYNC_EVERY_AT_FULL, Math.min(BEAT_SYNC_EVERY_AT_SLOW, Math.round(every)));
}

/**
 * Beat-sync slider also scales motion punch (pan/zoom/shake), not just beat cadence.
 */
export function beatSyncIntensityFromSongSpeed(speed) {
  const s = clampSongAnimationSpeed(speed);
  if (s <= 0) return 0;
  const slider = songAnimationSpeedToSlider(s);
  if (slider >= MOTION_SPEED_SLIDER_MAX) return BEAT_SYNC_INTENSITY_AT_FULL;
  if (slider <= 1) return BEAT_SYNC_INTENSITY_AT_SLOW;
  const t = smoothstep01((slider - 1) / (MOTION_SPEED_SLIDER_MAX - 1));
  return BEAT_SYNC_INTENSITY_AT_SLOW
    + (BEAT_SYNC_INTENSITY_AT_FULL - BEAT_SYNC_INTENSITY_AT_SLOW) * t;
}

export function songBeatSyncSpeedLabel(speed) {
  const s = clampSongAnimationSpeed(speed);
  if (s <= 0) return 'Stopped';
  const every = beatAnimateEveryFromSongSpeed(s);
  const pct = songAnimationSpeedToSlider(s);
  if (every <= 1) return `${pct}% · Every beat · Punchy`;
  return `${pct}% · Every ${every}th beat`;
}

export function songAnimationSettingsFromInput(input = {}) {
  const enabled = input?.song_default_animation_enabled;
  const raw = input?.song_animation_speed;
  return {
    defaultAnimationEnabled: enabled !== false,
    animationSpeed: raw == null ? SONG_ANIMATION_SPEED_DEFAULT : clampSongAnimationSpeed(raw),
  };
}

export function songAnimationSettingsToApiPayload(settings = {}) {
  return {
    song_default_animation_enabled: settings.defaultAnimationEnabled !== false,
    song_animation_speed: clampSongAnimationSpeed(settings.animationSpeed ?? SONG_ANIMATION_SPEED_DEFAULT),
  };
}

export function songAnimationSpeedLabel(speed) {
  return formatMotionSpeedLabel(speed, SONG_ANIMATION_SPEED_MAX);
}

export { MOTION_SPEED_SLIDER_MIN, MOTION_SPEED_SLIDER_MAX };
